import { makeAutoObservable, runInAction } from "mobx";
import WalletService, { IFetchFailedDepositTransactionsResponseDto, IWalletTransaction } from "../../../../services/api/wallet-service";
import { rootContainer, TYPE } from "../../../../services/container/root";
import CurrencyService from "../../../../services/currency-service";
import { isSet, toNumber } from "../../../../models/model-types-methods";
import {WalletStore} from "./wallet-store";

export class FailedDepositTransactionsStore {
    private _model?: IFetchFailedDepositTransactionsResponseDto;
    private _walletStore: WalletStore;
    private _isLoading = false;
    private _isRetryingLastDeposit = false;
    private _showPaymentMethodErrorModal: boolean = false;

    private get _walletService(): WalletService { return rootContainer.get<WalletService>(TYPE.WalletService); }
    private get _currencyService(): CurrencyService { return rootContainer.get<CurrencyService>(TYPE.CurrencyService);}

    constructor(walletStore: WalletStore) {
        makeAutoObservable(this, {}, { autoBind: true });
        this._walletStore = walletStore;
    }

    get isLoading(): boolean { return this._isLoading; }
    get isRetryingLastDeposit(): boolean { return this._isRetryingLastDeposit; }
    get isLoaded(): boolean { return isSet(this._model); }
    get transactions(): IWalletTransaction[] { return this._model?.failed_transactions ?? []; }
    get walletCurrencyCode(): string | null { return this._model?.wallet_currency_code ?? null; }
    get paymentCardBrand(): string | null { return this._model?.card_brand ?? null; }
    get paymentCardLast4(): string | null { return this._model?.card_last4 ?? null; }

    get amount(): number | null { return this.transactions[0]?.amount ?? null; }
    get amountFormattedWithCurrencySymbol(): string | null {
        return isSet(this.amount) && isSet(this.walletCurrencyCode)
            ? this._currencyService.formatCurrency(this.amount!, this.walletCurrencyCode!)
            : null;
    }
    get shouldShowPaymentMethodErrorModal(): boolean { return this._showPaymentMethodErrorModal; }

    public setModel(model: IFetchFailedDepositTransactionsResponseDto) {
        this._model = this._ensureValidModel(model);
    }

    public setShouldShowPaymentMethodErrorModal(v: boolean) {
        this._showPaymentMethodErrorModal = v;
        if (v === true) {
            this.load();
        }
    }

    public async load() {
        try {
            this._isLoading = true;
            const model = await this._walletService.fetchFailedDepositTransactions();
            runInAction(() => {
                this.setModel(model);
                this._isLoading = false;
            });
        }
        catch (e) {
            runInAction(() => {
                this._isLoading = false;
            });
        }
    }

    public async retryLastDepositHandler(): Promise<void> {
        this._isRetryingLastDeposit = true;
        try {
            const model = await this._walletService.retryLastDeposit();

            runInAction(() => {
                this.setModel(model);
                if (model.failed_transactions.length === 0) {
                    this.setShouldShowPaymentMethodErrorModal(false);
                    this._walletStore.load();
                }
            });
        }
        catch (e) {
            await this.load();
        }
        finally {
            runInAction(() => {
                this._isRetryingLastDeposit = false;
            })
        }
    }

    private _ensureValidModel(model: IFetchFailedDepositTransactionsResponseDto) {
        return {
            ...model,
            failed_transactions: model.failed_transactions.map((transaction: IWalletTransaction) => {
                return {
                    ...transaction,
                    amount: toNumber(transaction.amount),
                };
            })
        };
    }
}

export default FailedDepositTransactionsStore;